/* eslint-disable @typescript-eslint/naming-convention -- Ignore camelCase. */
import Transaction from './types/Transaction'
import { validateTransactionType } from './util'

type RawTransaction = { [key: string]: unknown } & {
  TransactionType?: string
  tx?: { [key: string]: unknown } & { TransactionType?: string }
  validated?: boolean
}

export { RawTransaction }

/**
 * The raw transaction object may have the transaction instructions fields on the same level
 * as the transaction fields. If so, this function places those fields into the tx property to make
 * a proper Transaction object.
 *
 * @param rawTransaction - The raw transaction.
 * @returns The fixed transaction.
 */
function unflattenTxn(rawTransaction: RawTransaction): RawTransaction {
  if (rawTransaction.tx) {
    return rawTransaction
  }

  const fields = [
    'date',
    'hash',
    'ledger_hash',
    'ledger_index',
    'meta',
    'validated',

    // alternative names for the above fields
    'metaData',
  ]

  const tx: RawTransaction = { ...rawTransaction }
  const unflattenedTransaction: RawTransaction = {}

  fields.forEach((field) => {
    if (tx[field]) {
      unflattenedTransaction[field] = tx[field]
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- This should be fine.
      delete tx[field]
    }
  })

  return {
    ...unflattenedTransaction,
    tx,
  }
}

const alternativeFieldName = {
  metaData: 'meta',
}

/**
 * Some properties may be referred to by multiple names (e.g., metadata or meta). This function renames
 * all of those fields into one consistent name.
 *
 * @param rawTransaction - The transaction in which to rename the fields of.
 * @returns The fixed transaction.
 */
function renameFields(rawTransaction: RawTransaction): RawTransaction {
  const renamedTransaction: RawTransaction = { ...rawTransaction }

  Object.entries(alternativeFieldName).forEach(([originalKey, renamedKey]) => {
    if (renamedTransaction[originalKey]) {
      renamedTransaction[renamedKey] = renamedTransaction[originalKey]
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- This should be fine.
      delete renamedTransaction[originalKey]
    }
  })

  return renamedTransaction
}

/**
 * Converts a raw JSON transaction into a Transaction object.
 * Accounts for small changes between the raw JSON and Transaction type.
 * Namely, converts the string TransactionType field into an enum,
 * and adds { validated: true } if the validated field is not provided.
 *
 * @throws Throws an error if input is missing tx and tx.TransactionType.
 *
 * @param rawTransaction - The transaction.
 * @returns The modified transaction.
 */
export default function rawJSONtoTransaction(
  rawTransaction: RawTransaction,
): Transaction {
  const txn = renameFields(unflattenTxn(rawTransaction))

  if (!txn.tx?.TransactionType) {
    throw new Error('Missing fields in transaction!')
  }

  const validatedTransactionInstructions = {
    ...txn.tx,
    TransactionType: validateTransactionType(txn.tx.TransactionType),
  }

  const validated = txn.validated === undefined ? true : txn.validated

  return {
    ...txn,
    tx: validatedTransactionInstructions,

    validated,
  } as Transaction
}
